import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";

/**
 * SignInRequired — shown on Chat/Talk when there is no auth cookie.
 * Replaces the old silent redirect that felt like a crash on mobile.
 *
 * Props:
 *   variant: "light" (Chat page, white bg) | "dark" (Talk page, graphite bg)
 *   title:   headline shown above the CTA
 *   subtitle optional supporting line
 */
export default function SignInRequired({
  variant = "light",
  title = "Sign in to continue",
  subtitle = "Your conversation with KALKI is private — a quick sign-in keeps it safe.",
}) {
  const navigate = useNavigate();
  const { login } = useAuth();
  const dark = variant === "dark";

  const bg = dark ? "#0E0E10" : "#FAFAFC";
  const dot = dark ? "#1F1F23" : "#DCE2EA";
  const ink = dark ? "#F8FAFC" : "#1C1C1C";
  const mute = dark ? "#A1A1AA" : "#676773";

  return (
    <div
      data-testid="signin-required"
      className="w-full flex flex-col items-center justify-center text-center px-6"
      style={{
        minHeight: "100dvh",
        background: bg,
        backgroundImage: `radial-gradient(${dot} 1px, transparent 1px)`,
        backgroundSize: "28px 28px",
        color: ink,
      }}
    >
      <div className="text-[11px] tracking-[0.32em] uppercase" style={{ color: mute }}>
        KALKI · LIFE COACH + AI ASTROLOGER
      </div>

      <h1
        className="font-serif font-light leading-[1.05] mt-5 max-w-[18ch]"
        style={{ fontSize: "clamp(34px, 8vw, 56px)", color: ink }}
      >
        {title}
      </h1>

      <p
        className="mt-5 max-w-[36ch] text-[15px] leading-relaxed"
        style={{ color: mute }}
      >
        {subtitle}
      </p>

      <div className="flex flex-col items-center gap-3 mt-8 w-full max-w-[320px]">
        <button
          data-testid="signin-required-primary"
          onClick={login}
          className="w-full inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full text-[15px] font-medium text-white"
          style={{
            background: "linear-gradient(135deg,#5D00FF,#FF2E9A)",
            boxShadow: "0 20px 50px rgba(93,0,255,0.3)",
          }}
        >
          Sign in with Google
        </button>
        <a
          data-testid="signin-required-create-account"
          href="/?signin=1"
          className="w-full inline-flex items-center justify-center px-6 py-3 rounded-full text-[13px] text-center"
          style={{
            color: dark ? "#F8FAFC" : "#1C1C1C",
            background: dark ? "rgba(255,255,255,0.08)" : "rgba(93,0,255,0.06)",
            border: dark ? "1px solid rgba(255,255,255,0.16)" : "1px solid rgba(93,0,255,0.25)",
          }}
        >
          Create account with email
        </a>
        <button
          data-testid="signin-required-back"
          onClick={() => navigate("/app")}
          className="w-full inline-flex items-center justify-center px-6 py-3 rounded-full text-[13px]"
          style={{
            color: dark ? "#F8FAFC" : "#1C1C1C",
            background: dark ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.7)",
            border: dark ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(0,0,0,0.1)",
          }}
        >
          Back to home
        </button>
      </div>
    </div>
  );
}
